// SiloKindPill + SiloKindConvertModal — Feb 2026 (Randy).
// One file, two exports so callers can pull just what they need.
//
// TYPES (Feb 2026):
//   public       — the classic "creator's public Q&A" silo. Also covers
//                  YouTube Twin silos (a flag on top of public).
//   personal     — private self-improvement silo. NO family features.
//   organizer    — private family/team calendar HQ. Household Setup,
//                  Reward store, Family leaderboard, Family digest live
//                  ONLY here. Existing personal silos with kid profiles
//                  were auto-migrated at boot.
//
// The pill renders inline (dashboard tile + manage page header). The
// convert modal is owner-only and never destroys data — hidden features
// just go dormant when the type flips.
import { useState } from "react";
import { X, Users, User, House, Broadcast, YoutubeLogo, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// ─────────────── metadata catalog ───────────────
export const SILO_KIND_META = {
  public: {
    label: "Public",
    short: "Public",
    Icon: Broadcast,
    pillCls: "bg-[#E3EFF7] text-[#2C6E92] border-[#B9CDE0]",
    blurb: "A creator's public Q&A silo — visitors, subscribers, fans.",
  },
  youtube_twin: {
    label: "YouTube Twin",
    short: "YT Twin",
    Icon: YoutubeLogo,
    pillCls: "bg-[#FDECEC] text-[#B54848] border-[#EBC9C9]",
    blurb: "A channel-cloned public silo where fans can ask your work anything.",
  },
  personal: {
    label: "Personal",
    short: "Personal",
    Icon: User,
    pillCls: "bg-[#F0E7D2] text-[#8A6A24] border-[#EAD9AE]",
    blurb: "Private self-improvement silo — just you and your key data.",
  },
  organizer: {
    label: "Organizer",
    short: "Organizer",
    Icon: Users,
    pillCls: "bg-[#EAF3EC] text-[#2F6B3A] border-[#B9D4BE]",
    blurb: "Private family / team HQ — calendar + household + rewards + digest.",
  },
};

// Resolve the effective kind from a silo doc (public+is_youtube_twin → "youtube_twin"
// for display purposes only; storage stays silo_kind=public + is_youtube_twin=true).
export function resolveSiloKind(silo) {
  if (!silo) return "public";
  const k = silo.silo_kind || "public";
  if (k === "public" && silo.is_youtube_twin) return "youtube_twin";
  return k;
}

// ─────────────── the pill ───────────────
export function SiloKindPill({ silo, size = "sm", onClick, className = "" }) {
  const key = resolveSiloKind(silo);
  const meta = SILO_KIND_META[key] || SILO_KIND_META.public;
  const { Icon } = meta;
  const sizes = size === "xs"
    ? "text-[9px] px-1.5 py-0.5"
    : "text-[10px] px-2 py-1";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      title={onClick ? "Change this silo's type" : meta.blurb}
      data-testid={`silo-kind-pill-${key}`}
      className={`${sizes} uppercase tracking-[0.16em] rounded-full border font-medium inline-flex items-center gap-1 ${meta.pillCls} ${onClick ? "hover:brightness-105 cursor-pointer" : "cursor-default"} ${className}`}>
      <Icon size={11} weight="duotone" /> {meta.short}
    </button>
  );
}

// ─────────────── convert modal ───────────────
// Props: siloId, currentKind ("public"|"personal"|"organizer"),
// currentYtTwin (bool), onClose, onSaved(updated).
// Jul 2026 (Randy Option B) — YouTube Twin is a flag on top of "public"
// under the hood, but users think of it as its own kind. So the convert
// modal now exposes it as a 4th top-level pick. Initial state is derived
// via resolveSiloKind so an existing YT-Twin silo lands on the YT-Twin
// row instead of Public + checkbox. Save handler unpacks it back into
// {silo_kind:"public", is_youtube_twin:true} because that's still how
// storage works.
export function SiloKindConvertModal({ siloId, currentKind, currentYtTwin, onClose, onSaved }) {
  const initialResolved = (currentKind === "public" && currentYtTwin) ? "youtube_twin" : (currentKind || "public");
  const [kind, setKind] = useState(initialResolved);
  const [busy, setBusy] = useState(false);
  const dirty = kind !== initialResolved;
  const save = async () => {
    setBusy(true);
    try {
      let patch;
      if (kind === "youtube_twin") {
        patch = { silo_kind: "public", is_youtube_twin: true };
      } else {
        patch = { silo_kind: kind, is_youtube_twin: false };
      }
      const r = await api.patch(`/silos/${siloId}`, patch);
      toast.success(`Silo type: ${SILO_KIND_META[resolveSiloKind(r.data)].label}.`);
      onSaved && onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't change the silo type.");
    } finally { setBusy(false); }
  };
  const OPTIONS = [
    { v: "public",       meta: SILO_KIND_META.public,       hint: "Anyone with the URL can ask questions." },
    { v: "youtube_twin", meta: SILO_KIND_META.youtube_twin, hint: "A public silo cloned from a YouTube channel — same feature set as Public, YT-Twin branding + toolkit." },
    { v: "personal",     meta: SILO_KIND_META.personal,     hint: "Private forever. Just you (or you + a co-owner). No family features." },
    { v: "organizer",    meta: SILO_KIND_META.organizer,    hint: "Private forever. Adds household, reward store, family leaderboard + weekly digest." },
  ];
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="silo-kind-convert-modal">
      <div className="bg-[#FDFCF8] rounded-2xl w-full max-w-lg shadow-2xl border hairline max-h-[90dvh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b hairline flex items-center justify-between shrink-0">
          <h2 className="font-serif-display text-lg">Change silo type</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F0F1EE]" data-testid="silo-kind-close"><X size={18} /></button>
        </div>
        <div className="p-5 flex-1 overflow-y-auto space-y-3">
          <p className="text-xs text-[#6B6A68] leading-relaxed">
            Flipping the type never destroys data — features hidden by a new type stay dormant. If you change your mind, flipping back restores everything exactly as it was.
          </p>
          {OPTIONS.map((o) => {
            const active = kind === o.v;
            const { Icon } = o.meta;
            return (
              <button key={o.v} onClick={() => setKind(o.v)} data-testid={`silo-kind-option-${o.v}`}
                className={`w-full text-left p-3 rounded-lg border-2 transition-all ${active ? "border-[#1C1B1A] bg-white" : "border-transparent bg-white/60 hover:bg-white"}`}>
                <div className="flex items-start gap-3">
                  <span className={`p-2 rounded-md ${o.meta.pillCls}`}><Icon size={16} weight="duotone" /></span>
                  <span className="flex-1 min-w-0">
                    <strong className="text-sm text-[#1C1B1A]">{o.meta.label}</strong>
                    <span className="block text-xs text-[#6B6A68] mt-0.5 leading-relaxed">{o.hint}</span>
                  </span>
                  {active && <span className="text-[9px] uppercase tracking-[0.14em] text-[#4A5D4E]">selected</span>}
                </div>
              </button>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t hairline shrink-0 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))] flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-2 rounded text-[#6B6A68]">Cancel</button>
          <button onClick={save} disabled={busy || !dirty} data-testid="silo-kind-save-btn"
            className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
            {busy ? "Saving…" : <>Change type <ArrowRight size={11} weight="bold" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SiloKindPill;
